import { useState } from "react";
import { supabase } from "../services/supabase";
import {
  validatePassword,
  validateConfirmPassword,
} from "../utils/validators";
import AuthLayout from "../components/AuthLayout";

export default function ResetPassword({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [strength, setStrength] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ---------------- Password strength (UI only) ---------------- */
  const calculateStrength = (pwd) => {
    let str = 0;
    if (pwd.length >= 8) str += 25;
    if (pwd.length >= 12) str += 25;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) str += 25;
    if (/[0-9]/.test(pwd)) str += 15;
    if (/[^a-zA-Z0-9]/.test(pwd)) str += 10;
    return Math.min(str, 100);
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setStrength(calculateStrength(pwd));
  };

  const getStrengthLabel = () => {
    if (strength >= 80) return "Excellent";
    if (strength >= 60) return "Good";
    if (strength >= 40) return "Fair";
    return "Weak";
  };

  const getStrengthColor = () => {
    if (strength >= 80) return "bg-[#06402B]";
    if (strength >= 60) return "bg-yellow-500";
    if (strength >= 40) return "bg-[#06402B]";
    return "bg-red-500";
  };

  /* ---------------- Submit ---------------- */
  const handleSubmit = async () => {
    // ✅ VALIDATION (single source of truth)
    const passwordError = validatePassword(password);
    const confirmError = validateConfirmPassword(
      password,
      confirmPassword
    );

    if (passwordError || confirmError) {
      setError(passwordError || confirmError);
      return;
    }

    setError("");
    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      // Clear hash from URL
      window.history.replaceState(null, "", window.location.pathname);
      onSuccess();
    } catch (err) {
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              Reset password
            </h1>
            <p className="text-gray-500">
              Please kindly set your new password
            </p>
          </div>

          <div className="space-y-6">
            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New password
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="••••••••••••"
                  className={`w-full px-4 py-3 rounded-full border 
                    ${error ? "border-red-500" : "border-gray-300"}
                    focus:outline-none focus:ring-2 focus:ring-[#06402B]`}
                />
                {password && strength >= 60 && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#06402B]">
                    ✓
                  </span>
                )}
              </div>

              {password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded ${
                          i < Math.ceil(strength / 25)
                            ? getStrengthColor()
                            : "bg-gray-200"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">
                    Password strength:{" "}
                    <span className="font-medium">
                      {getStrengthLabel()}
                    </span>
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Re-enter password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                placeholder="••••••••••••"
                className={`w-full px-4 py-3 rounded-full border 
                  ${error ? "border-red-500" : "border-gray-300"}
                  focus:outline-none focus:ring-2 focus:ring-[#06402B]`}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !password || !confirmPassword}
              className="w-full bg-[#06402B] text-white py-3 rounded-full font-medium hover:bg-green-900 transition-colors disabled:opacity-50"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        </div>
      </main>

      
    </AuthLayout>
  );
}
